/**
 * Pure Instagram URL + id helpers, extracted from the `Instagram` class (./index.ts).
 *
 * WHY THIS FILE EXISTS: the `Instagram` class module-imports redisClient and the five
 * scraper providers (Star/Rocket/Upleap/Bulk/IGFlash) + url-join at the top of index.ts,
 * so ANY client component that imported the class merely to call one of these PURE string
 * helpers dragged the entire IG data-fetch layer into the client bundle. These helpers are
 * now the single source of truth; the `Instagram` class delegates to them (so every existing
 * server-side + cross-app caller of `Instagram.getProxiedImageUrl()` etc. keeps working
 * unchanged), and client components import them directly from here.
 *
 * ZERO runtime dependencies — only JS/web globals (atob, encodeURIComponent, BigInt, Date).
 * Do NOT add redis/provider/network imports here.
 */

/**
 * Primary method: Use our own API proxy to bypass CORS.
 * More reliable than Google Translate and has no rate limits.
 */
export function getProxiedImageUrl(url: string, useApiProxy: boolean = true): string {
	if (!url || !url.includes("http")) return url;

	// Already proxied - return as-is
	if (url.includes('/api/ig-proxy') || url.includes('upgrow.com/ig-assets')) return url;

	// Check if it's an Instagram CDN URL that needs proxying
	const isInstagramCdn = url.includes('cdninstagram.com') ||
		url.includes('fbcdn.net') ||
		url.includes('instagram.') ||
		url.includes('scontent');

	if (!isInstagramCdn) return url;

	// Primary: Use our API proxy (no rate limits, more reliable)
	if (useApiProxy) {
		return `/api/ig-proxy?url=${encodeURIComponent(url)}`;
	}

	// Fallback: Use Google Translate proxy (legacy method)
	return decodeInstagramMediaURLLegacy(url);
}

/**
 * Legacy method: Google Translate proxy (kept as fallback).
 * Warning: Subject to rate limiting (429 errors).
 */
export function decodeInstagramMediaURLLegacy(url: string): string {
	if (!url.includes("http")) return url;
	if (url.includes(atob("LnRyYW5zbGF0ZS5nb29n"))) return url;
	const p = url.split("/"); let t = "";
	for (let i = 0; i < p.length; i++)
		t += i == 2 ? p[i].replaceAll("-", "--").replaceAll(".", "-") + atob("LnRyYW5zbGF0ZS5nb29n") + "/" : i != p.length - 1 ? p[i] + "/" : p[i];
	return t;
}

/**
 * Main entry point for proxying Instagram images.
 * Uses the API proxy by default instead of Google Translate.
 */
export function decodeInstagramMediaURL(url: string): string {
	return getProxiedImageUrl(url, true);
}

export function decodeInstagramURLtoSelfHosted(url: string): string {
	// Extract filename using regex pattern matching for both URL types
	const filename = url.includes('instagram.fmad')
		? url.split('/').pop()?.split('?')[0]
		: url.match(/\/([^\/]+?\.(jpg|mp4|webp))/i)?.[1];

	if (!filename) return url;

	// Build self-hosted URL with parameters
	return `https://www.upgrow.com/ig-assets/${filename}?url=${encodeURIComponent(url)}&time=${Math.floor(Date.now() / 1000 + 86400)}`;
}

/**
 * Instagram media id -> shortcode.
 *
 * The id arrives in one of two shapes and both must work:
 *   - a numeric graphql media id ("3576543210987654321", or "<pk>_<user_pk>") -> base64-encode it
 *   - a shortcode already ("Derb0kplXps", what the Flash API returns since 2026-10-29) -> use as-is
 *
 * Never throws: BigInt() on a shortcode threw "Cannot convert Derb0kplXps to a BigInt" and
 * crashed the whole analytics page. A value that is neither shape yields "" (the caller
 * then degrades to no link).
 */
export function graphqlIDToShortcode(graphqlID: string): string {
	const raw = graphqlID == null ? '' : String(graphqlID).trim();
	if (!raw) return '';

	// Already a shortcode (non-numeric): pass it through untouched — shortcodes may contain
	// '-'/'_' from their own alphabet, so they must NOT be split on the underscore.
	if (!/^\d+(_\d+)*$/.test(raw)) return raw;

	const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
	let id = BigInt(raw.split('_')[0]); // numeric graphql id: keep the part before the underscore
	let shortcode = '';
	while (id > BigInt(0)) {
		let remainder = id % BigInt(64);
		id = id / BigInt(64);
		shortcode = alphabet.charAt(Number(remainder)) + shortcode;
	}
	return shortcode;
}
